import React, { useState, useMemo, useCallback } from 'react';
import cN from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { RootState } from '@Redux/search/store';
import { I_FlightFilter, I_FlightFilterSelected } from '@Redux/search/Flight/interface';
import UseTempData from '../useHook/UseTempData';
import UseDestCount from '../useHook/UseDestCount';
import { setFlightSelected, setFlightLightboxDest } from '@Redux/search/Flight/action';
import { rebuildDestionationData } from '@utils/utils';
import { SliderInput } from '@lion-design/liontravel';
import ShowAllDestination from '../common/ShowAllDestination';
import MultiCheckbox from '@components/Common/MultiCheckbox';
import { CheckboxItem } from '@components/Common/CheckboxItem';
import * as ShowMore from '@components/Common/ShowMore';
import styles from '../../styles.module.scss';

enum PrivateFilterKey {
    selectedDestinationID = 'selectedDestinationID',
    selectedDepartureID = 'selectedDepartureID',
    selectedDepartureMonth = 'selectedDepartureMonth',
}

enum MutiCheckboxKeys {
    // 目的地參數
    CityID = 'CityID',
    CityName = 'CityName',
    CountryID = 'CountryID',
    CountryName = 'CountryName',
    filterBy = 'filterBy',
    parentId = 'parentId',
    value = 'value',
    text = 'text',
}

const Flight: React.FC = (props) => {
    const dispatch = useDispatch();
    const { sendEmitApi, CityLength } = useSelector((state: RootState) => state.search.reducer);
    const {
        FlightFilterStore,
        FlightFilterSelected,
    }: {
        FlightFilterStore: I_FlightFilter;
        FlightFilterSelected: I_FlightFilterSelected;
    } = useSelector((state: RootState) => state.search.FlightReducer);
    const { PriceMax, PriceMin, DestinationList, DepartureList, DepartureMonthList, LightboxDestination } =
        FlightFilterStore; //所有篩選資料
    const { selectedPrices, selectedDestinationID, selectedDepartureID, selectedDepartureMonth } = FlightFilterSelected;
    const isPC = useMediaQuery({ query: '(min-width: 980px)' });
    const [showDestination, setShowDestination] = useState(false); //測試 destination光箱開啟
    const [tempSelectData] = UseTempData([showDestination]);
    const destinationCount = UseDestCount(selectedDestinationID); //計算城市跟國家
    // 更動進階篩選、排序時重發api，api組成參數都在這
    // const params = UseCombineApi(TicketFilterSelected, TicketFilterSelected);
    // useEffect(() => {
    // if (sendEmitApi) {
    //     dispatch(setFilterDataLoading(true));
    //     let apiUrl = 'searchactivity';
    //     //由redux的sendEmitApi值決定是否打api
    //     dispatch(getData({ apiUrl, params }));
    //     // setFirstLoading(false);
    // }
    // }, [TicketFilterSelected]);
    const HandleCheckBox =
        (filterKey: keyof typeof PrivateFilterKey, checkedValue: string | number) => () => {
            const prevValue = Object.values(FlightFilterSelected[filterKey]); //取selectedFilter物件特定的值
            const selected = prevValue.indexOf(checkedValue); // 確認裡面的id索引存不存在
            if (selected === -1) {
                prevValue.push(checkedValue);
                dispatch(setFlightSelected({ [filterKey]: prevValue, Page: 1, PageSize: 10 }));
            } else {
                prevValue.splice(selected, 1);
                dispatch(setFlightSelected({ [filterKey]: prevValue, Page: 1, PageSize: 10 }));
            }
        };
    const handleMouseUpChange = (value: number[], onDragBtn: string) => {
        // 價格更動時確認最小跟最大值是不是跟初始一樣，一樣則變為空陣列(無參數)，因api有傳進階篩選值時，篩選data不會回傳
        const isInitPrice = value[0] === PriceMin && value[1] === PriceMax;
        const mutablePrice = isInitPrice ? [] : [value[0], value[1]];
        // 這邊可以發api
        dispatch(setFlightSelected({ selectedPrices: mutablePrice, Page: 1, PageSize: 10 }));
    };
    const combinSelectedDestination = useCallback(() => {
        // 針對光箱開啟、pc或M版回傳不同ui的checkbox資料
        // if (!isPC) return selectedDestinationID;
        if (!isPC) return selectedDestinationID;
        if (!sendEmitApi) return tempSelectData?.selectedDestinationID;
        return selectedDestinationID;
    }, [tempSelectData, selectedDestinationID]);

    const origindestinationData = useMemo(() => {
        const rebuildData = rebuildDestionationData(
            //參數順序不能錯
            DestinationList,
            MutiCheckboxKeys.CountryName,
            MutiCheckboxKeys.CountryID,
            MutiCheckboxKeys.CityName,
            MutiCheckboxKeys.CityID
        );
        return rebuildData;
    }, [DestinationList]);

    const destinationData = useMemo(() => {
        // 紀錄selectedDestinationID的map值 再從origindestinationData拿出未選到的值(原始的值有初始目的地、光箱所有城市(可能有也可能沒有))
        const parentIdList = new Map(); //點了篩選後的parentId紀錄
        const lightboxData = new Map(); //光箱城市資料
        selectedDestinationID.map((val) => {
            if (!parentIdList.has(val.parentId)) {
                parentIdList.set(val.parentId, val.parentId);
            }
        });
        let combine: any[] = [];
        // 拿出光箱所有資料
        // 有點選的城市國家比對光箱所有城市並取出 (前提光箱目的地的資料是所有包含一開始初始資料，否則初始資料城市會被光箱城市資料蓋過)
        LightboxDestination.forEach((val, key) => {
            if (parentIdList.has(val.ParentId)) {
                combine = [...combine, ...val.values];
                lightboxData.set(val.ParentId, val);
            }
        });

        // 比對原始城市(要照初始城市的順序排列)
        origindestinationData.forEach((val, key) => {
            if (Object.keys(LightboxDestination).length === 0) {
                // 如果勾選的有初始城市，但沒有光箱目的地資料則設定初始資料parentId，有光箱目的地資料則拿出非勾選的原始城市
                combine = [...combine, ...val.values];
            }
            // 光箱城市資料比對原始城市相符的parentId，相符就更改originData的子城市資料
            if (parentIdList.has(val.ParentId)) {
                val.values = combine.filter((obj) => {
                    if (obj.parentId === val.ParentId) return obj;
                });
                lightboxData.delete(val.ParentId); // 更改後刪掉，因未符合初始資料的光箱城市要放在最後
            }
        });
        origindestinationData.push(...Array.from(lightboxData.values()));

        return Object.entries(origindestinationData).map(([key, val]) => {
            return { ParentId: key, ...val };
        });
    }, [tempSelectData, origindestinationData]);

    // 好像沒用到可以刪掉
    // const productTypeData = useMemo(() => {
    //     const rebuildData = rebuildDestionationData(
    //         //參數順序不能錯
    //         ProductTypeList,
    //         MutiCheckboxKeys.ParentName,
    //         MutiCheckboxKeys.ParentID,
    //         MutiCheckboxKeys.KindName,
    //         MutiCheckboxKeys.Kind
    //     );
    //     return Object.entries(rebuildData).map(([key, val]) => {
    //         return { ParentId: key, ...val };
    //     });
    // }, [ProductTypeList]);

    // if (destinationData.length === 0) return null;
    return (
        <>
            <p className={styles.Title}>您的預算</p>
            <SliderInput
                currency="TWD"
                minPrice={PriceMin}
                maxPrice={PriceMax}
                setRange={selectedPrices.length === 0 ? [PriceMin, PriceMax] : selectedPrices}
                setFormatPrice={true}
                updatePrice={(value: number[], onDragBtn: string) => {
                    handleMouseUpChange(value, onDragBtn);
                }}
            />
            {destinationData.length > 0 && (
                <>
                    <p className={styles.Title}>
                        目的地{' '}
                        <span className={styles.Title_cityCount}>
                            已勾選:{destinationCount}/200
                        </span>
                    </p>
                    {destinationData.map((objData, i) => (
                        <MultiCheckbox
                            multiData={objData}
                            checkBoxSelected={combinSelectedDestination()}
                            reduxSelectedKey="selectedDestinationID"
                            whichStore={setFlightSelected}
                            isDisabled={destinationCount >= 200}
                            destinationCount={destinationCount}
                            // CityLength={CityLength}
                            key={'dest' + i}
                        />
                    ))}
                    <ShowAllDestination
                        checkBoxSelected={selectedDestinationID}
                        whichStore={setFlightSelected}
                        productType="etk"
                        initialDestination={DestinationList}
                        destinationCount={destinationCount}
                        showDestination={showDestination}
                        setShowDestination={setShowDestination}
                        setLightboxDest={setFlightLightboxDest}
                    />
                </>
            )}
            {/* 出發地checkbox */}
            {DepartureList.length > 0 && <p className={styles.Title}>出發地</p>}
            <ul className={cN(styles.singleCheckBox_Wrap, styles.CheckBoxes)}>
                    {DepartureList.map((obj: any) => {
                        const selected = selectedDepartureID.indexOf(obj.CityID); // 確認裡面的id索引存不存在
                        return (
                            <li
                                className={styles.singleCheckBox_Content}
                                key={obj.CityID}
                                onClick={HandleCheckBox('selectedDepartureID', obj.CityID)}
                            >
                                <CheckboxItem
                                    text={obj.CityName}
                                    value={obj.CityID}
                                    isDisable={false}
                                    isCheck={selected}
                                />
                            </li>
                        );
                    })}
            </ul>
            
            {/* 出發時間checkbox*/}
            {DepartureMonthList.length > 0 && <p className={styles.Title}>出發時間</p>}
            <ul className={cN(styles.singleCheckBox_Wrap, styles.CheckBoxes)}>
                {DepartureMonthList.map((month: number) => {
                    const selected = selectedDepartureMonth.indexOf(month); // 確認裡面的id索引存不存在
                    return (
                        <li
                            className={styles.singleCheckBox_Content}
                            key={month}
                            onClick={HandleCheckBox('selectedDepartureMonth', month)}
                        >
                            <CheckboxItem
                                text={month + '月'}
                                value={month}
                                isDisable={false}
                                isCheck={selected}
                            />
                        </li>
                    );
                })}
            </ul>
        </>
    );
};
export default Flight;
